/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
import { css } from '@firebolt-dev/css'
import { useEffect, useState } from 'react'
import { CheckIcon, LinkIcon } from 'lucide-react'

import { COLORS, ICON_SIZES, RADII, TYPE, space } from '../theme'

// Привязка мира к внешнему аккаунту.
//
// Подаём НЕ как «войдите» — играть можно и гостем, требовать вход на входе
// значит терять людей. Подаём как то, чем это на самом деле является:
// страховка от потери мира при смене устройства.
//
// Провайдеры берём с сервера: если ключи не настроены, список пустой и
// блок не рисуется вовсе. Кнопка, ведущая в ошибку, хуже её отсутствия.

const LABELS = { google: 'Google', github: 'GitHub' }

// Что рассказать человеку, когда он вернулся с той стороны.
const RESULT_MESSAGES = {
  ok: { tone: 'good', text: 'Мир закреплён за твоим аккаунтом. Теперь он найдётся с любого устройства.' },
  access_denied: { tone: 'muted', text: 'Вход отменён — ничего не изменилось, можно играть дальше гостем.' },
  STATE_EXPIRED: { tone: 'warn', text: 'Вход занял слишком много времени. Попробуй ещё раз.' },
  STATE_MISMATCH: { tone: 'warn', text: 'Проверка безопасности не прошла. Начни вход заново.' },
  STATE_MISSING: { tone: 'warn', text: 'Проверка безопасности не прошла. Начни вход заново.' },
  PROVIDER_NOT_CONFIGURED: { tone: 'warn', text: 'Этот способ входа сейчас недоступен.' },
}
const FALLBACK_RESULT = { tone: 'warn', text: 'Войти не получилось. Мир и прогресс не пострадали — попробуй ещё раз.' }

export function AccountLink() {
  const [providers, setProviders] = useState([])
  const [linked, setLinked] = useState([])
  const [result, setResult] = useState(null)

  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const response = await fetch('/api/auth/providers')
        const data = await response.json()
        if (alive) setProviders(Array.isArray(data?.providers) ? data.providers : [])
      } catch {
        // молчим: отсутствие кнопок не должно ломать вход в мир
      }
      try {
        const response = await fetch('/api/auth/identities', { credentials: 'include' })
        if (response.ok) {
          const data = await response.json()
          if (alive) setLinked(Array.isArray(data?.identities) ? data.identities : [])
        }
      } catch {
        /* пусто */
      }
    }
    load()
    return () => {
      alive = false
    }
  }, [])

  // Результат возврата от провайдера приходит в адресе — показываем и
  // сразу вычищаем, чтобы не висел при обновлении страницы.
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location?.search || '')
    const login = params.get('login')
    if (!login) return
    setResult(RESULT_MESSAGES[login] || FALLBACK_RESULT)
    params.delete('login')
    const query = params.toString()
    globalThis.history?.replaceState?.({}, '', `${globalThis.location.pathname}${query ? `?${query}` : ''}`)
  }, [])

  if (!providers.length && !result) return null

  const linkedIds = new Set(linked.map(item => item.provider))
  const available = providers.filter(provider => !linkedIds.has(provider.id))

  return (
    <div css={wrapCss}>
      {result && <p css={resultCss(result.tone)}>{result.text}</p>}

      {linkedIds.size > 0 && (
        <p css={linkedCss}>
          <CheckIcon size={ICON_SIZES.sm} />
          Мир закреплён: {[...linkedIds].map(id => LABELS[id] || id).join(', ')}
        </p>
      )}

      {available.length > 0 && (
        <>
          <p css={hintCss}>
            Сейчас мир живёт только в этом браузере. Закрепи его — и он найдётся с телефона и после чистки истории.
          </p>
          <div css={rowCss}>
            {available.map(provider => (
              <a key={provider.id} href={`/api/auth/oauth/${provider.id}/start`} css={buttonCss}>
                <LinkIcon size={ICON_SIZES.sm} />
                {LABELS[provider.id] || provider.label || provider.id}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const wrapCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space(1)};
  margin-top: ${space(4)};
  max-width: 34rem;
  text-align: center;
`

const hintCss = css`
  margin: 0;
  color: ${COLORS.textSecondary};
  font-size: ${TYPE.caption.size};
  line-height: 1.45;
`

const linkedCss = css`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: ${space(0)};
  color: ${COLORS.success};
  font-size: ${TYPE.caption.size};
`

const rowCss = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${space(1)};
`

const buttonCss = css`
  display: inline-flex;
  align-items: center;
  gap: ${space(0)};
  padding: ${space(1)} ${space(3)};
  min-height: 2.75rem;
  border: 1px solid ${COLORS.border};
  border-radius: ${RADII.md};
  color: ${COLORS.text};
  text-decoration: none;
  font-size: ${TYPE.caption.size};
  letter-spacing: 0.02em;
  background: rgba(255, 255, 255, 0.04);
  transition: border-color 120ms ease, background 120ms ease;
  &:hover,
  &:focus-visible {
    border-color: ${COLORS.cyan};
    background: rgba(255, 255, 255, 0.08);
  }
`

const resultCss = tone => css`
  margin: 0;
  font-size: ${TYPE.caption.size};
  line-height: 1.45;
  color: ${tone === 'good' ? COLORS.success : tone === 'warn' ? COLORS.warning : COLORS.textSecondary};
`
